import * as React from "react";
// MUI
import { grey } from "@mui/material/colors";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

// Third Party
import { Player } from "@lottiefiles/react-lottie-player";
import { User as Auth0User } from "@auth0/auth0-react";
import { Link } from "react-router-dom";

// Internal
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_MY_PUBLICATIONS,
  GET_USER_COLLECTIONS,
} from "../../data/graphql/queries";
import {
  CREATE_COLLECTION,
  ADD_ITEM_TO_COLLECTION,
} from "../../data/graphql/mutations";
import { PublicationType, CollectionType } from "../../data/types";
import PublicationCard from "../../components/PublicationCard";
import en from "../../data/en.json";
import Loading from "../../assets/animations/loading.json";

interface MyPublicationProps {
  user: Auth0User;
}

interface MyPublicationsTabType {
  index: number;
  label: string;
}

export default function MyPublications(props: MyPublicationProps) {
  const { loading, data } = useQuery(GET_MY_PUBLICATIONS, {
    variables: { user_auth0_id: props.user.sub },
  });

  const {
    loading: collectionsLoading,
    data: collectionsData,
    refetch: refetchCollections,
  } = useQuery(GET_USER_COLLECTIONS, {
    variables: {
      user_auth0_id: props.user.sub,
      include_private: true,
    },
  });

  const [createCollection, { loading: createCollectionLoading }] =
    useMutation(CREATE_COLLECTION);

  const [addItemToCollection, { loading: addItemLoading }] = useMutation(
    ADD_ITEM_TO_COLLECTION
  );

  const myPubliationTabTypes: MyPublicationsTabType[] = [
    { index: 0, label: en.userDashboard.myPublicationsSection.pending },
    { index: 1, label: en.userDashboard.myPublicationsSection.approved },
  ];

  const [currentTab, setCurrentTab] = React.useState<MyPublicationsTabType>(
    myPubliationTabTypes[0]
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedPublication, setSelectedPublication] =
    React.useState<PublicationType | null>(null);
  const [isCreatingCollection, setIsCreatingCollection] = React.useState(false);
  const [addingToCollectionId, setAddingToCollectionId] = React.useState<
    string | null
  >(null);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [newCollectionTitle, setNewCollectionTitle] = React.useState("");
  const [newCollectionDescription, setNewCollectionDescription] =
    React.useState("");
  const [newCollectionPublic, setNewCollectionPublic] = React.useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(
      myPubliationTabTypes.find((tabType) => tabType.index === newValue) ||
        currentTab
    );
  };

  const handleAddToCollectionClick = (
    event: React.MouseEvent<HTMLElement>,
    publication: PublicationType
  ) => {
    event.preventDefault(); // Prevent navigation to publication detail
    event.stopPropagation(); // Stop event bubbling
    setAnchorEl(event.currentTarget);
    setSelectedPublication(publication);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedPublication(null);
  };

  const handleSelectCollection = async (collection: CollectionType) => {
    if (!selectedPublication) {
      console.error("No publication selected");
      return;
    }

    setAddingToCollectionId(collection.id);

    try {
      const result = await addItemToCollection({
        variables: {
          collection_uuid: collection.uuid,
          item_type: "Publication",
          item_id: selectedPublication.id,
        },
      });

      if (result.data?.addItemToCollection?.collectionItem) {
        console.log(
          `Successfully added publication "${selectedPublication.title}" to collection "${collection.title}"`
        );
        // Refetch collections to update the item counts
        await refetchCollections();
      } else if (result.data?.addItemToCollection?.errors?.length > 0) {
        console.error(
          "Error adding to collection:",
          result.data.addItemToCollection.errors
        );
      }
    } catch (error) {
      console.error("Error adding publication to collection:", error);
    } finally {
      setAddingToCollectionId(null);
    }

    handleCloseMenu();
  };

  const handleCreateNewCollection = () => {
    if (!selectedPublication) return;

    // Set default title based on publication
    setNewCollectionTitle(`Collection - ${selectedPublication.title}`);
    setNewCollectionDescription("");
    setNewCollectionPublic(false);
    setShowCreateModal(true);
    // Close menu but keep selected publication for the modal
    setAnchorEl(null);
  };

  const handleCreateCollection = async () => {
    if (!selectedPublication || !newCollectionTitle.trim()) return;

    setIsCreatingCollection(true);

    try {
      // Prepare mutation variables
      const variables: any = {
        user_auth0_id: props.user.sub,
        title: newCollectionTitle.trim(),
        description: newCollectionDescription.trim() || null,
        public: newCollectionPublic,
      };

      // Only include item parameters if we have a valid selected publication
      if (selectedPublication && selectedPublication.id) {
        variables.item_type = "Publication";
        variables.item_id = selectedPublication.id;
      }

      const result = await createCollection({
        variables,
      });

      if (result.data?.createCollection) {
        console.log(
          "Collection created successfully:",
          result.data.createCollection
        );
        // Refetch collections to update the list
        await refetchCollections();
        // Close modal and reset form
        setShowCreateModal(false);
        setNewCollectionTitle("");
        setNewCollectionDescription("");
        setNewCollectionPublic(false);
        setSelectedPublication(null);
      }
    } catch (error) {
      console.error("Error creating collection:", error);
    } finally {
      setIsCreatingCollection(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewCollectionTitle("");
    setNewCollectionDescription("");
    setNewCollectionPublic(false);
    setSelectedPublication(null);
  };

  if (loading || collectionsLoading) {
    return (
      <Player
        autoplay={true}
        loop={true}
        src={Loading}
        style={{ height: "300px", width: "300px" }}
      />
    );
  }

  const myPublications: PublicationType[] =
    data.myPublications as PublicationType[];
  const userCollections: CollectionType[] =
    (collectionsData?.userCollections as CollectionType[]) || [];
  const selectedPublications: PublicationType[] = myPublications.filter(
    (publication: PublicationType) => publication.status === currentTab.label
  );

  return (
    <React.Fragment>
      <Tabs value={currentTab.index} onChange={handleTabChange}>
        <Tab label={en.userDashboard.myPublicationsSection.pending} />
        <Tab label={en.userDashboard.myPublicationsSection.approved} />
      </Tabs>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        {selectedPublications.length > 0 &&
          selectedPublications.map(
            (publication: PublicationType, index: number) => (
              <Box
                key={index}
                sx={{
                  m: 2,
                  p: 2,
                  border: 1,
                  borderColor: grey[300],
                  width: 750,
                  position: "relative",
                }}
              >
                <Link
                  to={`/publications/${publication.uuid}`}
                  style={{ textDecoration: "none", color: "black" }}
                >
                  <PublicationCard publication={publication} />
                </Link>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BookmarkAddIcon />}
                  onClick={(e) => handleAddToCollectionClick(e, publication)}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    backgroundColor: "white",
                    "&:hover": {
                      backgroundColor: grey[50],
                    },
                  }}
                >
                  Add to Collection
                </Button>
              </Box>
            )
          )}
        {selectedPublications.length === 0 && (
          <Typography variant="h3" color={grey[300]} sx={{ mt: 2 }}>
            {en.common.noPublication}
          </Typography>
        )}

        {/* Collection Selection Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          PaperProps={{
            style: {
              maxHeight: 400,
              width: 250,
            },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ px: 2, py: 1, fontWeight: "bold" }}
          >
            Select Collection
          </Typography>
          {userCollections.map((collection: CollectionType) => (
            <MenuItem
              key={collection.id}
              onClick={() => handleSelectCollection(collection)}
              disabled={addingToCollectionId === collection.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <ListItemText
                primary={collection.title}
                secondary={`${collection.itemsCount} items`}
              />
              {addingToCollectionId === collection.id && (
                <CircularProgress size={20} />
              )}
            </MenuItem>
          ))}
          <MenuItem
            onClick={handleCreateNewCollection}
            sx={{
              borderTop: `1px solid ${grey[200]}`,
            }}
          >
            <AddIcon sx={{ mr: 1 }} />
            <ListItemText primary="Create New Collection" />
          </MenuItem>
        </Menu>

        {/* Create Collection Modal */}
        <Modal
          open={showCreateModal}
          onClose={handleCloseCreateModal}
          aria-labelledby="create-collection-modal"
          aria-describedby="create-collection-form"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 500,
              bgcolor: "background.paper",
              border: "1px solid #ccc",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Typography variant="h6" component="h2">
                Create New Collection
              </Typography>
              <IconButton onClick={handleCloseCreateModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {selectedPublication && (
              <Box
                mb={3}
                p={2}
                sx={{ backgroundColor: grey[50], borderRadius: 1 }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Adding publication:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedPublication.title}
                </Typography>
              </Box>
            )}

            <Box
              component="form"
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              <TextField
                label="Collection Title"
                value={newCollectionTitle}
                onChange={(e) => setNewCollectionTitle(e.target.value)}
                fullWidth
                required
                variant="outlined"
                disabled={isCreatingCollection}
              />

              <TextField
                label="Description (Optional)"
                value={newCollectionDescription}
                onChange={(e) => setNewCollectionDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                disabled={isCreatingCollection}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={newCollectionPublic}
                    onChange={(e) => setNewCollectionPublic(e.target.checked)}
                    disabled={isCreatingCollection}
                  />
                }
                label="Make this collection public"
              />

              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button
                  onClick={handleCloseCreateModal}
                  disabled={isCreatingCollection}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateCollection}
                  disabled={isCreatingCollection || !newCollectionTitle.trim()}
                  variant="contained"
                  startIcon={isCreatingCollection ? null : <AddIcon />}
                >
                  {isCreatingCollection ? "Creating..." : "Create Collection"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
    </React.Fragment>
  );
}
